/** Wide playfield shell: fixed side rails, center column grows with viewport. */
export const GROW_PLAYFIELD_SHELL_LAYOUT = {
    outerPx: { xs: 1, sm: 1.5, lg: 2 },
    gridGap: { xs: 2, lg: 1.5 },
    gridColumns: {
        xs: '1fr',
        lg: 'minmax(200px, 240px) minmax(0, 1fr) minmax(220px, 280px)',
    },
    sidePanelPadding: 2,
    playColumnPadding: 1.5,
};

/** Shared Arcade visual tokens — matches Snake / PuzzleMaster. */
export const ARCADE_THEME = {
    bg: '#0a0a0a',
    cardBg: '#18181b',
    border: '#333',
    primary: '#2cbb5d',
    secondary: '#a1a1aa',
    accent: '#ef4743',
    grid: '#222',
    gold: '#fbbf24',
};

/**
 * User-facing error text: drop stack traces / paths / verbose server dumps.
 */
export function sanitizeArcadeError(raw, fallback = 'Something went wrong. Try again.') {
    if (raw == null || raw === '') return fallback;
    const s = String(raw).trim();
    if (!s) return fallback;
    const lines = s.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const isTrace = /traceback \(most recent call last\)/i.test(s)
        || (lines.length > 2 && /file ["']|stack trace/i.test(s));
    if (isTrace) {
        const errLine = [...lines].reverse().find((l) => /error|exception|failed/i.test(l));
        if (errLine) {
            const compact = errLine.replace(/\s+/g, ' ').slice(0, 180);
            return compact || fallback;
        }
        return fallback;
    }
    if (s.length > 180) return `${s.slice(0, 177)}…`;
    return s;
}

export const UPLOAD_SESSION_EXPIRED_MESSAGE = 'Session expired — sign in again.';

/**
 * Message for a failed agent upload. Prefers the server's own error text
 * (e.g. guest 403 "Guest accounts cannot upload agents…", gateway 401
 * "Please register (free) and log in…"); the session-expired text is only
 * used for a bare 401. `needsAccount` is true for 401/403 so the UI can
 * offer register / log-in links.
 */
export function uploadErrorInfo(status, body, fallback = 'Upload failed.') {
    const raw = body && typeof body === 'object' ? body.error || body.message : null;
    const needsAccount = status === 401 || status === 403;
    if (raw) return { message: sanitizeArcadeError(raw, fallback), needsAccount };
    if (status === 401) return { message: UPLOAD_SESSION_EXPIRED_MESSAGE, needsAccount };
    if (status === 403) {
        return {
            message: 'This account cannot upload agents. Please register (free) and log in.',
            needsAccount,
        };
    }
    return { message: fallback, needsAccount: false };
}

export function formatArcadeTimestamp(ts) {
    if (!ts) return '—';
    let date;
    if (typeof ts === 'string' && ts.includes('T') && !ts.endsWith('Z') && !ts.includes('+')) {
        date = new Date(`${ts}Z`);
    } else {
        date = new Date(ts);
    }
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function leaderboardEntryScore(entry) {
    if (entry == null) return 0;
    if (entry.score != null) return entry.score;
    if (entry.player_score != null) return entry.player_score;
    if (entry.total_reward != null) return entry.total_reward;
    if (entry.bankroll != null) return entry.bankroll;
    if (entry.distance != null) return entry.distance;
    if (entry.revealed_safe != null) return entry.revealed_safe;
    if (entry.pairs_completed != null) return entry.pairs_completed;
    if (entry.hits != null) return entry.hits;
    if (entry.moves != null) return entry.moves;
    if (entry.steps != null) return entry.steps;
    return 0;
}

/** Elapsed milliseconds for time-ranked games (e.g. Puzzle Master). */
export function leaderboardEntryElapsedTime(entry) {
    if (entry == null) return null;
    const candidates = [entry.elapsed_time, entry.elapsed_ms, entry.time_ms, entry.time];
    for (const v of candidates) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
}

/** Minimum elapsed time across leaderboard rows (human finishes when flagged). */
export function bestLeaderboardElapsedTime(entries, { humanOnly = true } = {}) {
    if (!Array.isArray(entries) || !entries.length) return null;
    let best = null;
    for (const entry of entries) {
        if (humanOnly) {
            const finishedBy = entry.finished_by;
            if (finishedBy && finishedBy !== 'human') continue;
        }
        const elapsed = leaderboardEntryElapsedTime(entry);
        if (elapsed == null) continue;
        if (best == null || elapsed < best) best = elapsed;
    }
    return best;
}

export function leaderboardEntryName(entry, idx = 0) {
    const src = String(entry?.source || '').toLowerCase();
    if (src === 'manual' || src === 'human') {
        return (
            entry?.username ||
            entry?.display_name ||
            entry?.player_id ||
            'Player'
        );
    }
    return (
        entry?.agent ||
        entry?.agent_id ||
        entry?.username ||
        entry?.player_id ||
        `Agent ${idx + 1}`
    );
}
